/*
 * <nav-accordion>: the collapsed link groups of the compact mobile navigation.
 *
 * Wraps the stacked groups of one drawer panel (snippets/header-drawer.liquid,
 * compact mode). Each group is a `<button data-nav-accordion-toggle aria-controls>`
 * over a `hidden` panel. Rules from the navigation concept v4:
 *
 * - groups start collapsed, one group is open at a time;
 * - the overview row ("Wintersport entdecken") is a plain link outside this element,
 *   never an expansion control;
 * - Escape closes the open group first and returns focus to its toggle. The event
 *   is stopped there, so Dawn's drawer controller (assets/global.js, MenuDrawer)
 *   only sees the NEXT Escape, which closes the submenu, and the one after that
 *   closes the drawer. Each step restores focus on its own.
 *
 * Deliberately not built on <details>/<summary>: MenuDrawer binds a click handler to
 * every summary and every button inside the drawer, and its Escape handler closes
 * the closest open <details>, so a nested disclosure would fight it on both counts.
 * The toggles carry `data-nav-accordion-toggle`, which MenuDrawer.bindEvents skips.
 */

const TOGGLE = '[data-nav-accordion-toggle]';
const GROUP = '[data-nav-accordion-group]';

class NavAccordion extends HTMLElement {
  connectedCallback() {
    this.onClick = this.handleClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onSubmenuToggle = this.handleSubmenuToggle.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('keydown', this.onKeyDown);

    /* A submenu that closes takes its groups back to the collapsed start state, so
       reopening the department shows the four headings again, not the last group. */
    this.submenu = this.closest('details');
    if (this.submenu) this.submenu.addEventListener('toggle', this.onSubmenuToggle);

    this.toggles.forEach((toggle) => this.setOpen(toggle, toggle.getAttribute('aria-expanded') === 'true'));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keyup', this.onKeyUp);
    this.removeEventListener('keydown', this.onKeyDown);
    if (this.submenu) this.submenu.removeEventListener('toggle', this.onSubmenuToggle);
  }

  get toggles() {
    return Array.from(this.querySelectorAll(TOGGLE));
  }

  get openToggle() {
    return this.toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true') || null;
  }

  panelFor(toggle) {
    const id = toggle.getAttribute('aria-controls');
    if (!id) return null;
    const panel = this.ownerDocument.getElementById(id);
    return panel && this.contains(panel) ? panel : null;
  }

  setOpen(toggle, open) {
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    const panel = this.panelFor(toggle);
    if (panel) panel.hidden = !open;
    const group = toggle.closest(GROUP);
    if (group) group.classList.toggle('is-open', open);
  }

  open(toggle) {
    this.toggles.forEach((other) => {
      if (other !== toggle && other.getAttribute('aria-expanded') === 'true') this.setOpen(other, false);
    });
    this.setOpen(toggle, true);
  }

  closeAll() {
    this.toggles.forEach((toggle) => this.setOpen(toggle, false));
  }

  handleClick(event) {
    const toggle = event.target.closest(TOGGLE);
    if (!toggle || !this.contains(toggle)) return;
    event.preventDefault();
    if (toggle.getAttribute('aria-expanded') === 'true') {
      this.setOpen(toggle, false);
    } else {
      this.open(toggle);
    }
  }

  /* Dawn listens on keyup for Escape (MenuDrawer.onKeyUp and onKeyUpEscape). While a
     group is open the key belongs to this element: the keydown is swallowed so the
     browser does nothing else with it, the keyup closes the group and stops there. */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    if (this.openToggle) event.stopPropagation();
  }

  handleKeyUp(event) {
    if (event.key !== 'Escape') return;
    const toggle = this.openToggle;
    if (!toggle) return;
    event.stopPropagation();
    event.preventDefault();
    this.setOpen(toggle, false);
    toggle.focus();
  }

  handleSubmenuToggle() {
    if (this.submenu && !this.submenu.open) this.closeAll();
  }
}

if (!customElements.get('nav-accordion')) customElements.define('nav-accordion', NavAccordion);
